import React from "react";

import { Card } from "../Card";
import { Popper, PopperProps } from "../Popper";
import { TabIndexVisibility } from "../TabIndexVisibility";

export interface PopoverProps {
  /**
   * Closes when the user clicks away from the popover
   * @default true
   */
  closeOnClickAway?: boolean;
  /**
   * Ignores close on click away used for specific scenarios
   * @default false
   */
  ignoreCloseOnClickAway?: boolean;
  /**
   * When using closeOnClickAway, this function is called when the user clicks away. Signature: `(open: boolean) => void;`
   */
  onSetOpen?: (open: boolean) => void;
  /**
   * The element to attach this popover to
   */
  parent: JSX.Element;
  /**
   * The popover contents
   */
  popoverComponent: React.ReactNode;
  /**
   * Whether to render the popover or not
   * @default false
   */
  isOpen?: boolean;
  /**
   * CSS class name to add to Popover Card element
   * @default ""
   */
  className?: string;
  /**
   * Position of the popper
   * @default "bottom"
   */
  position?:
    | "top-start"
    | "top"
    | "top-end"
    | "right-start"
    | "right"
    | "right-end"
    | "bottom-start"
    | "bottom"
    | "bottom-end"
    | "left-start"
    | "left"
    | "left-end";
  /**
   * Arrow that points toward the reference element.
   * @default false
   */
  displayArrow?: boolean;
  /**
   * Animation that will be used everytime the popover is displayed
   */
  animate?: "height" | "visibility";
  /**
   * Class that will be applied to the arrow that points toward the reference element
   */
  arrowClassName?: string;
  /**
   * Props to pass to the Popper component
   */
  popperProps?: Partial<PopperProps>;
  /**
   * If true, the popover will be rendered in a portal. Portals are useful for rendering components outside of their parent component's DOM hierarchy, such as modals or tooltips.
   * If false, the popover will be rendered in place. This can be useful for performance reasons or if the popover needs to interact with its parent component's DOM.
   * @default true
   */
  renderInPortal?: boolean;
  /**
   * Classname to add to the wrapper div
   */
  wrapperClassName?: string;
}

/**
 * A large panel of content attached to a specific element on the page.
 */
export function Popover({
  closeOnClickAway = true,
  ignoreCloseOnClickAway = false,
  parent,
  isOpen = false,
  popoverComponent,
  onSetOpen,
  className = "",
  position = "bottom",
  displayArrow = false,
  animate,
  arrowClassName,
  popperProps,
  renderInPortal = true,
  wrapperClassName,
}: React.PropsWithChildren<PopoverProps>): JSX.Element {
  return (
    <Popper
      position={position}
      closeOnClickAway={closeOnClickAway}
      ignoreCloseOnClickAway={ignoreCloseOnClickAway}
      isOpen={isOpen}
      onSetOpen={onSetOpen}
      displayArrow={displayArrow}
      animate={animate}
      arrowClassName={arrowClassName}
      renderInPortal={renderInPortal}
      className={wrapperClassName}
      popupElement={
        <TabIndexVisibility visible={isOpen}>
          <Card
            data-testid={`popover-${isOpen ? "open" : "closed"}`}
            className={className}
          >
            {popoverComponent}
          </Card>
        </TabIndexVisibility>
      }
      {...popperProps}
    >
      {parent}
    </Popper>
  );
}
